import React from 'react'

interface HexLoaderProps {
  size?: 'sm' | 'md' | 'lg'
  text?: string
  className?: string
}

const sizes = {
  sm: { width: 40, height: 46 },
  md: { width: 60, height: 69 },
  lg: { width: 80, height: 92 },
}

export function HexLoader({ size = 'md', text, className = '' }: HexLoaderProps) {
  const { width, height } = sizes[size]

  return (
    <div className={`flex flex-col items-center justify-center ${className}`}>
      <div
        className="hex-loader"
        style={{ width, height }}
      >
        <svg viewBox="0 0 80 92" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
          {/* Hexagon outline */}
          <path
            className="hex-outer"
            d="M40 2 L74 24 L74 68 L40 90 L6 68 L6 24 Z"
          />
          {/* Y shape inside */}
          <path
            className="hex-y"
            d="M40 28 L40 50 M40 50 L40 65 M40 50 L25 35 M40 50 L55 35"
          />
        </svg>
      </div>
      {text && (
        <p className="mt-4 text-sm text-[var(--text-muted)] tracking-wider uppercase">
          {text}<span className="loading-dots" />
        </p>
      )}

      <style>{`
        .hex-loader {
          position: relative;
        }

        .hex-outer {
          fill: none;
          stroke: var(--primary, #facc15);
          stroke-width: 2;
          stroke-dasharray: 300;
          stroke-dashoffset: 300;
          animation: draw-hex 1.5s ease-in-out infinite;
          filter: drop-shadow(0 0 8px color-mix(in srgb, var(--primary, #facc15) 40%, transparent));
        }

        .hex-y {
          fill: none;
          stroke: var(--primary, #facc15);
          stroke-width: 2;
          stroke-linecap: round;
          stroke-dasharray: 100;
          stroke-dashoffset: 100;
          animation: draw-y 1.5s ease-in-out infinite;
          animation-delay: 0.3s;
          filter: drop-shadow(0 0 6px color-mix(in srgb, var(--primary, #facc15) 50%, transparent));
        }

        .hex-loader {
          animation: glow-pulse 1.5s ease-in-out infinite;
        }

        @keyframes draw-hex {
          0% {
            stroke-dashoffset: 300;
            opacity: 0.3;
          }
          50% {
            stroke-dashoffset: 0;
            opacity: 1;
          }
          100% {
            stroke-dashoffset: -300;
            opacity: 0.3;
          }
        }

        @keyframes draw-y {
          0% {
            stroke-dashoffset: 100;
            opacity: 0;
          }
          30% {
            opacity: 0;
          }
          50% {
            stroke-dashoffset: 0;
            opacity: 1;
          }
          70% {
            opacity: 1;
          }
          100% {
            stroke-dashoffset: -100;
            opacity: 0;
          }
        }

        @keyframes glow-pulse {
          0%, 100% {
            filter: drop-shadow(0 0 10px color-mix(in srgb, var(--primary, #facc15) 20%, transparent));
          }
          50% {
            filter: drop-shadow(0 0 25px color-mix(in srgb, var(--primary, #facc15) 60%, transparent))
                    drop-shadow(0 0 50px color-mix(in srgb, var(--primary, #facc15) 30%, transparent));
          }
        }

        .loading-dots::after {
          content: '';
          animation: dots 1.5s steps(4, end) infinite;
        }

        @keyframes dots {
          0% { content: ''; }
          25% { content: '.'; }
          50% { content: '..'; }
          75% { content: '...'; }
          100% { content: ''; }
        }
      `}</style>
    </div>
  )
}

// Full page loading screen
export function FullPageLoader({ text = 'Loading' }: { text?: string }) {
  return (
    <div className="min-h-screen bg-[var(--bg-main)] flex items-center justify-center">
      <HexLoader size="lg" text={text} />
    </div>
  )
}
